import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PenLine, ArrowRight, Globe } from 'lucide-react';
import { fetchPerspectiveGroups } from '../../apis/perspectives.api';
import MiniSpectrum from './MiniSpectrum';

const LeafIcon = ({ size = 11 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75" />
  </svg>
);

/**
 * Shown on the Perspectives tab — explains how community responses work.
 */
export function YourPerspectiveMatters() {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-5">
      <h3
        className="text-sm font-extrabold text-slate-900 mb-1.5"
        style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
      >
        Add What the Discourse Misses
      </h3>
      <p className="text-[13px] text-slate-500 leading-relaxed mb-3.5">
        AI perspectives synthesize what people are saying across news, social media, and public
        debate. But no synthesis captures lived experience. That's where you come in.
      </p>
      <div className="flex flex-col gap-2">
        {[
          'Read how real public debate is being framed',
          "Spot what's missing from your perspective",
          'Write it — earn >50 twigs to get featured'
        ].map((step, i) => (
          <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold text-green-600 shrink-0"
              style={{ background: 'rgba(5,150,105,0.08)' }}
            >
              {i + 1}
            </span>
            {step}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Shown on the Perspectives tab — lists events needing community responses.
 */
export function NeedsCommunityVoices({ groups }) {
  const navigate = useNavigate();
  const needsVoices = (groups || []).filter((g) => (g.communityResponseCount || 0) < 3);

  if (needsVoices.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
      <div className="px-[18px] pt-4 pb-3">
        <h3
          className="text-sm font-extrabold text-slate-900"
          style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
        >
          Needs Community Voices
        </h3>
        <p className="text-xs text-slate-400 mt-0.5">
          These topics have few or no community responses yet
        </p>
      </div>

      {needsVoices.slice(0, 4).map((event) => (
        <div
          key={event.id || event.slug}
          className="px-[18px] py-3 border-t border-stone-100 cursor-pointer transition-colors"
          onClick={() => navigate(`/perspectives/${event.slug}`)}
        >
          <div className="flex items-center gap-1.5 mb-1">
            <span
              className="text-[10px] font-bold tracking-wide uppercase"
              style={{ color: '#b45309', fontFamily: "'JetBrains Mono', monospace" }}
            >
              {event.category}
            </span>
          </div>
          <div
            className="text-[13px] font-bold text-slate-900 leading-tight mb-1.5"
            style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
          >
            {event.title}
          </div>
          <div className="flex items-center justify-between">
            <span
              className="text-[11px] text-slate-400"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              {event.communityResponseCount || 0} community responses
            </span>
            <span
              className="text-[11px] text-green-600 font-bold flex items-center gap-1 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                const params = new URLSearchParams();
                if (event.id) params.set('perspectiveGroupId', event.id);
                if (event.slug) params.set('perspectiveGroupSlug', event.slug);
                if (event.title)
                  params.set('perspectiveGroupTitle', encodeURIComponent(event.title));
                navigate(`/article/create?${params.toString()}`);
              }}
            >
              <PenLine size={11} /> Write yours
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Compact sidebar widget shown when NOT on the Perspectives tab.
 * Promotes perspectives from other feed tabs.
 */
export function CompactPerspectivesSidebar() {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['perspectiveGroups'],
    queryFn: () => fetchPerspectiveGroups({ limit: 5 }),
    staleTime: 60_000
  });

  const groups = data?.data || [];
  if (groups.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
      {/* Header */}
      <div className="px-[18px] pt-4 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #059669, #10b981)' }}
          >
            <Globe size={14} className="text-white" />
          </div>
          <div>
            <div
              className="text-sm font-extrabold text-slate-900"
              style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
            >
              Perspectives
            </div>
            <div className="text-[11px] text-slate-400">Multi-angle coverage</div>
          </div>
        </div>
        <span
          className="text-xs text-green-600 font-semibold cursor-pointer hover:underline"
          onClick={() => navigate('/?tab=perspectives')}
        >
          View all
        </span>
      </div>

      {/* Event items */}
      {groups.slice(0, 3).map((event) => {
        const perspectives = event.perspectives || [];
        return (
          <div
            key={event.id || event.slug}
            className="px-[18px] py-3.5 border-t border-stone-100 cursor-pointer transition-colors"
            onClick={() => navigate(`/perspectives/${event.slug}`)}
          >
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
              <span
                className="text-[10px] font-bold tracking-wide uppercase text-amber-600"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                Active
              </span>
              <span className="text-[10px] text-stone-300">·</span>
              <span className="text-[10px] text-slate-400">{event.category}</span>
            </div>

            <div
              className="text-[13.5px] font-bold text-slate-900 leading-tight mb-2"
              style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
            >
              {event.title}
            </div>

            <MiniSpectrum perspectives={perspectives} compact />

            <div className="flex items-center justify-between mt-1">
              <span
                className="text-[11px] text-slate-400"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {perspectives.length} perspectives
              </span>
              {(event.totalTwigs || 0) > 0 && (
                <span
                  className="flex items-center gap-1 text-[11px] text-green-600"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  <LeafIcon size={11} /> {(event.totalTwigs || 0).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        );
      })}

      {/* Footer */}
      <div className="px-[18px] py-3 border-t border-stone-100 text-center">
        <span
          className="text-xs text-green-600 font-semibold cursor-pointer inline-flex items-center gap-1 hover:underline"
          onClick={() => navigate('/?tab=perspectives')}
        >
          See all {groups.length} active topics <ArrowRight size={12} />
        </span>
      </div>
    </div>
  );
}
